import { useState, useEffect, useCallback } from 'react';
import {
  getAudioDevices,
  getVideoDevices,
  getAllDevices,
  getDefaultDevice,
  setupDeviceChangeListener,
  isDeviceAvailable,
  getDeviceErrorMessage,
  stopStream
} from '../utils/deviceManager';

/**
 * Filter devices to remove duplicate internal microphones
 * Keep only default internal device and all external devices
 */
const filterAudioDevices = (devices) => {
  const filtered = [];
  const internalDevices = [];
  const externalDevices = [];

  // Separate internal and external devices
  devices.forEach(device => {
    const deviceName = device.label.toLowerCase();
    
    // Check if it's an internal microphone (Intel Smart Sound Technology)
    if (deviceName.includes('intel') && 
        deviceName.includes('smart sound') && 
        deviceName.includes('microphone')) {
      internalDevices.push(device);
    } else {
      // External or other devices
      externalDevices.push(device);
    }
  });

  // From internal devices, only keep the "Default" one
  const defaultInternal = internalDevices.find(device => 
    device.label.toLowerCase().includes('default')
  );
  
  if (defaultInternal) {
    filtered.push(defaultInternal);
  }

  // Add all external devices
  filtered.push(...externalDevices);

  return filtered;
};

/**
 * Custom hook for managing audio and video devices
 * @param {Object} options - Configuration options
 * @param {boolean} options.autoInitialize - Whether to initialize devices on mount
 * @param {boolean} options.listenForChanges - Whether to listen for device changes
 * @returns {Object} Device management state and functions
 */
export const useDeviceManager = (options = {}) => {
  const {
    autoInitialize = true,
    listenForChanges = true
  } = options;

  // Audio device state
  const [availableAudioDevices, setAvailableAudioDevices] = useState([]);
  const [selectedAudioDevice, setSelectedAudioDevice] = useState(null);
  const [audioPermissionGranted, setAudioPermissionGranted] = useState(false);
  const [audioError, setAudioError] = useState(null);

  // Video device state
  const [availableVideoDevices, setAvailableVideoDevices] = useState([]);
  const [selectedVideoDevice, setSelectedVideoDevice] = useState(null);
  const [videoPermissionGranted, setVideoPermissionGranted] = useState(false);
  const [videoError, setVideoError] = useState(null);

  // Loading states
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);
  const [isLoadingVideo, setIsLoadingVideo] = useState(false);

  /**
   * Load audio devices
   */
  const loadAudioDevices = useCallback(async () => {
    try {
      setIsLoadingAudio(true);
      setAudioError(null);
      
      const allDevices = await getAudioDevices();
      
      // Filter to get only default internal + external devices
      const filteredDevices = filterAudioDevices(allDevices);
      
      setAvailableAudioDevices(filteredDevices);
      setAudioPermissionGranted(true);
      
      // Set default device if none selected
      if (!selectedAudioDevice && filteredDevices.length > 0) {
        const defaultDevice = getDefaultDevice(filteredDevices, 'audio');
        setSelectedAudioDevice(defaultDevice);
      }
      
      return filteredDevices;
    } catch (error) {
      setAudioPermissionGranted(false);
      setAudioError(getDeviceErrorMessage(error, 'audio'));
      return [];
    } finally {
      setIsLoadingAudio(false);
    }
  }, [selectedAudioDevice]);

  /**
   * Load video devices
   */
  const loadVideoDevices = useCallback(async () => {
    try {
      setIsLoadingVideo(true);
      setVideoError(null);
      
      const devices = await getVideoDevices();
      setAvailableVideoDevices(devices);
      setVideoPermissionGranted(true);
      
      // Set default device if none selected
      if (!selectedVideoDevice && devices.length > 0) {
        const defaultDevice = getDefaultDevice(devices, 'video');
        setSelectedVideoDevice(defaultDevice);
      }
      
      return devices;
    } catch (error) {
      setVideoPermissionGranted(false);
      setVideoError(getDeviceErrorMessage(error, 'video'));
      return [];
    } finally {
      setIsLoadingVideo(false);
    }
  }, [selectedVideoDevice]);

  /**
   * Load all devices
   */
  const loadAllDevices = useCallback(async () => {
    try {
      setIsLoadingAudio(true);
      setIsLoadingVideo(true);
      setAudioError(null);
      setVideoError(null);
      
      const { audio, video } = await getAllDevices();
      
      // Filter audio devices to remove duplicates
      const filteredAudio = filterAudioDevices(audio);
      
      setAvailableAudioDevices(filteredAudio);
      setAvailableVideoDevices(video);
      setAudioPermissionGranted(true);
      setVideoPermissionGranted(true);
      
      // Set default devices if none selected
      if (!selectedAudioDevice && filteredAudio.length > 0) {
        const defaultAudioDevice = getDefaultDevice(filteredAudio, 'audio');
        setSelectedAudioDevice(defaultAudioDevice);
      }
      
      if (!selectedVideoDevice && video.length > 0) {
        const defaultVideoDevice = getDefaultDevice(video, 'video');
        setSelectedVideoDevice(defaultVideoDevice);
      }
      
      return { audio: filteredAudio, video };
    } catch (error) {
      setAudioPermissionGranted(false);
      setVideoPermissionGranted(false);
      setAudioError(getDeviceErrorMessage(error, 'audio'));
      setVideoError(getDeviceErrorMessage(error, 'video'));
      return { audio: [], video: [] };
    } finally {
      setIsLoadingAudio(false);
      setIsLoadingVideo(false);
    }
  }, [selectedAudioDevice, selectedVideoDevice]);

  /**
   * Select audio device
   */
  const selectAudioDevice = useCallback((device) => {
    if (!device || !device.deviceId) {
      return;
    }
    
    setSelectedAudioDevice(device);
    setAudioError(null);
  }, []);

  /**
   * Select video device
   */
  const selectVideoDevice = useCallback((device) => {
    if (!device || !device.deviceId) {
      return;
    }
    
    setSelectedVideoDevice(device);
    setVideoError(null);
  }, []);

  /**
   * Handle device changes
   */
  const handleDeviceChange = useCallback(async () => {
    // Check if currently selected devices are still available
    const audioDevices = await loadAudioDevices();
    const videoDevices = await loadVideoDevices();
    
    // Check audio device availability
    if (selectedAudioDevice && audioDevices.length > 0) {
      const isAudioDeviceAvailable = isDeviceAvailable(selectedAudioDevice, audioDevices);
      if (!isAudioDeviceAvailable) {
        const defaultDevice = getDefaultDevice(audioDevices, 'audio');
        if (defaultDevice) {
          selectAudioDevice(defaultDevice);
        }
      }
    }
    
    // Check video device availability
    if (selectedVideoDevice && videoDevices.length > 0) {
      const isVideoDeviceAvailable = isDeviceAvailable(selectedVideoDevice, videoDevices);
      if (!isVideoDeviceAvailable) {
        const defaultDevice = getDefaultDevice(videoDevices, 'video');
        if (defaultDevice) {
          selectVideoDevice(defaultDevice);
        }
      }
    }
  }, [selectedAudioDevice, selectedVideoDevice, loadAudioDevices, loadVideoDevices, selectAudioDevice, selectVideoDevice]);

  /**
   * Clear errors
   */
  const clearAudioError = useCallback(() => {
    setAudioError(null);
  }, []);

  const clearVideoError = useCallback(() => {
    setVideoError(null);
  }, []);

  /**
   * Reset all devices
   */
  const resetDevices = useCallback(() => {
    setAvailableAudioDevices([]);
    setAvailableVideoDevices([]);
    setSelectedAudioDevice(null);
    setSelectedVideoDevice(null);
    setAudioPermissionGranted(false);
    setVideoPermissionGranted(false);
    setAudioError(null);
    setVideoError(null);
  }, []);

  // Initialize devices on mount
  useEffect(() => {
    if (autoInitialize) {
      loadAllDevices();
    }
  }, [autoInitialize, loadAllDevices]);

  // Setup device change listener
  useEffect(() => {
    if (listenForChanges) {
      const cleanup = setupDeviceChangeListener(handleDeviceChange);
      return cleanup;
    }
  }, [listenForChanges, handleDeviceChange]);

  return {
    // Audio devices
    availableAudioDevices,
    selectedAudioDevice,
    audioPermissionGranted,
    audioError,
    isLoadingAudio,
    loadAudioDevices,
    selectAudioDevice,
    clearAudioError,
    
    // Video devices
    availableVideoDevices,
    selectedVideoDevice,
    videoPermissionGranted,
    videoError,
    isLoadingVideo,
    loadVideoDevices,
    selectVideoDevice,
    clearVideoError,
    
    // Combined functions
    loadAllDevices,
    resetDevices,
    handleDeviceChange
  };
};